/**
 * CRITICAL: Polyfills for Android 4.x support
 * This file must be imported FIRST before any other code
 *
 * Note: whatwg-fetch and abortcontroller-polyfill have built-in feature detection
 * and won't override native implementations in modern browsers
 */

import { restoreNativeJson } from "./lib/nativeJson";

if (typeof window !== "undefined") {
  restoreNativeJson(JSON, window.__POS_NATIVE_JSON__);
}

// Polyfill performance.now() for Android 4.1.2
// The performance object exists but performance.now() doesn't
if (typeof performance !== "undefined" && !performance.now) {
  const startTime = Date.now();
  performance.now = function () {
    return Date.now() - startTime;
  };
}

// Polyfill fetch API and AbortController for Android 4.x
// These polyfills check for native support before applying
import "whatwg-fetch";
import "abortcontroller-polyfill/dist/abortcontroller-polyfill-only";

// Override Headers after whatwg-fetch loads, in case it captured a broken reference
// This ensures the polyfill is used during fetch calls
import { Headers as HeadersPolyfill } from "headers-polyfill";

if (typeof window !== "undefined") {
  window.Headers = HeadersPolyfill;
}

// KitKat specific polyfills for RTK Query compatibility
// Object.entries polyfill
if (!Object.entries) {
  Object.entries = function (obj: any) {
    const ownProps = Object.keys(obj);
    let i = ownProps.length;
    const resArray = new Array(i); // preallocate the Array
    while (i--) resArray[i] = [ownProps[i], obj[ownProps[i]]];

    return resArray;
  };
}

// Object.fromEntries polyfill
if (!Object.fromEntries) {
  Object.fromEntries = function (iterable: any) {
    const obj: any = {};
    for (const [key, val] of iterable) {
      obj[key] = val;
    }
    return obj;
  };
}

// Array.find polyfill
if (!Array.prototype.find) {
  Array.prototype.find = function (this: any[], predicate: any) {
    if (this == null) {
      throw new TypeError("Array.prototype.find called on null or undefined");
    }
    if (typeof predicate !== "function") {
      throw new TypeError("predicate must be a function");
    }
    const list = Object(this);
    const length = parseInt(list.length as string) || 0;
    const thisArg = arguments[1];
    for (let i = 0; i < length; i++) {
      const element = list[i];
      if (predicate.call(thisArg, element, i, list)) {
        return element;
      }
    }
    return undefined;
  };
}

// Array.includes polyfill
if (!Array.prototype.includes) {
  Array.prototype.includes = function (this: any[], searchElement: any) {
    const O = Object(this);
    const len = parseInt(O.length) || 0;
    if (len === 0) {
      return false;
    }
    const n = parseInt(arguments[1]) || 0;
    let k;
    if (n >= 0) {
      k = n;
    } else {
      k = len + n;
      if (k < 0) {
        k = 0;
      }
    }
    for (; k < len; k++) {
      if (O[k] === searchElement) {
        return true;
      }
    }
    return false;
  };
}

// String.includes polyfill
if (!String.prototype.includes) {
  String.prototype.includes = function (
    this: string,
    search: string,
    start?: number
  ) {
    if (typeof start !== "number") {
      start = 0;
    }

    if (start! + search.length > this.length) {
      return false;
    } else {
      return this.indexOf(search, start) !== -1;
    }
  };
}

// Enhanced fetch for KitKat - ensure proper JSON handling
if (typeof window !== "undefined" && window.fetch) {
  const originalFetch = window.fetch;

  window.fetch = function (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {

    // For KitKat, we need to be extra careful about headers and body
    if (init && init.method && init.method.toUpperCase() !== "GET") {
      // Ensure we have proper headers object
      if (!init.headers) {
        init.headers = {};
      }

      // Convert Headers object to plain object for KitKat compatibility
      if (typeof (init.headers as any).get === "function") {
        const plainHeaders: any = {};
        (init.headers as Headers).forEach((value, key) => {
          plainHeaders[key] = value;
        });
        init.headers = plainHeaders;
      }

      // Ensure JSON body is properly handled
      if (init.body && typeof init.body === "object" && init.body !== null) {
        init.body = JSON.stringify(init.body);

        // Force the content-type header for KitKat
        (init.headers as any)["Content-Type"] =
          "application/json; charset=utf-8";
      }

      // Ensure Content-Length is set for KitKat compatibility
      if (init.body && typeof init.body === "string") {
        (init.headers as any)["Content-Length"] = init.body.length.toString();
      }
    }


    return originalFetch
      .call(this, input, init)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}

// Ensure JSON is working properly on KitKat
if (typeof window !== "undefined" && window.JSON) {
  const originalStringify = JSON.stringify;

  JSON.stringify = function (value: any, replacer?: any, space?: any) {
    const result = originalStringify.call(this, value, replacer, space);
    return result;
  };
}

// Test KitKat capabilities

// Add a test function to debug KitKat requests
if (typeof window !== "undefined") {
  (window as any).testKitKatFetch = async function () {

    const testData = {
      username: "stag",
      password: "password",
      clientId: 1,
    };

    try {
      const response = await fetch(
        "https://api.dev.t-soft.io/api/v2/retail/auth/login?source=retail",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
            Accept: "application/json",
          },
          body: JSON.stringify(testData),
        }
      );

      const result = await response.json();

      return result;
    } catch (error) {
      return error;
    }
  };

}


// Polyfill requestAnimationFrame for Android 4.0
// Must use raf/polyfill to actually modify the global window object
// Just importing 'raf' doesn't polyfill the global scope
import "raf/polyfill";
